import React from 'react';
import { Table } from '@crisellevaldez/speculo-ui';
import { HiHeart } from 'react-icons/hi';
import { formatDate } from '../../../../utils/dateTime';
import { formatCurrency } from '../../../../utils/formatters';
import { TablePagination } from '../../../common/TablePagination';
import { TableCard } from '../../../common/TableCard';
import { ActivityTableHeader } from './ActivityTableHeader';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useTenant } from '../../../../hooks/client/tenant/useTenant';
import { useContactSavedPropertiesGQL } from '../../../../hooks/graphql/contacts/useContactSavedPropertiesGQL';
import type { GQLSavedPropertyItem } from '../../../../hooks/graphql/contacts/useContact';
import { isPreviewContactId, PREVIEW_SAVED_PROPERTIES } from '../../../../pages/previewMockData';

interface SavedPropertiesTabProps {
  contactId: string;
  isActive?: boolean;
}

interface SavedPropertyAddress {
  full?: string;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

const getSavedPropertyAddress = (address: GQLSavedPropertyItem['address']) => {
  if (!address || typeof address !== 'string') return '';

  try {
    const parsed = JSON.parse(address) as SavedPropertyAddress;
    if (parsed && typeof parsed === 'object') {
      return (
        parsed.full ||
        parsed.address ||
        [parsed.street, parsed.city, parsed.state, parsed.zip].filter(Boolean).join(', ').trim() ||
        [parsed.city, parsed.state, parsed.zip].filter(Boolean).join(' ').trim()
      );
    }
  } catch {
    // Address is already a plain display string.
  }

  return address;
};

const getSavedPropertyUrl = (
  websiteDomain: string,
  organizationId: string,
  item: GQLSavedPropertyItem
) => {
  if (!websiteDomain || !organizationId || !item.propertyId) return '';

  if (/^https?:\/\//i.test(item.propertyId)) {
    return item.propertyId;
  }

  return `https://${websiteDomain}/property/${item.propertyId}?tenant_id=${organizationId}${item.mlsBoardCode ? `&mls_osn=${item.mlsBoardCode}` : ''}`;
};
const getColumns = (websiteDomain: string, organizationId: string) => [
  {
    header: 'Property',
    key: 'property',
    width: '300px',
    cell: (item: GQLSavedPropertyItem) => {
      const propertyUrl = getSavedPropertyUrl(websiteDomain, organizationId, item);
      const propertyAddress = getSavedPropertyAddress(item.address) || '-';

      return (
        <div className="flex items-start gap-3">
          {item.primaryPhotoUrl ? (
            <img src={item.primaryPhotoUrl} alt="Property" className="w-16 h-16 object-cover rounded-md" />
          ) : (
            <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
              <HiHeart className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
          <div>
            {propertyUrl ? (
              <a href={propertyUrl} target="_blank" rel="noopener noreferrer" className="font-medium underline" title={propertyAddress}>{propertyAddress}</a>
            ) : (
              <div className="font-medium">{propertyAddress}</div>
            )}
            <div className="text-sm text-muted-foreground">{item.bedrooms || 0} bed • {item.bathrooms || 0} bath • {item.squareFeet || 0} sqft</div>
            <div className="text-sm text-muted-foreground">{item.listingOffice || 'Unknown Office'}</div>
          </div>
        </div>
      );
    },
  },
  {
    header: 'MLS',
    key: 'mls',
    width: '120px',
    cell: (item: GQLSavedPropertyItem) => (
      <div className="text-sm">
        <div>{item.mlsNumber ? `#${item.mlsNumber}` : 'N/A'}</div>
        <div className="text-muted-foreground">{item.mlsBoardCode || ''}</div>
      </div>
    ),
  },
  {
    header: 'Price',
    key: 'price',
    width: '120px',
    cell: (item: GQLSavedPropertyItem) => <div className="font-medium">{formatCurrency(item.price)}</div>,
  },
  {
    header: 'Saved At',
    key: 'savedAt',
    width: '180px',
    cell: (item: GQLSavedPropertyItem) => {
      const { organization } = useOrganizationStore.getState();
      return formatDate(item.savedAt, organization?.timezone);
    },
  },
];

export const SavedPropertiesTab: React.FC<SavedPropertiesTabProps> = ({ contactId }) => {
  const { organization } = useOrganizationStore();
  const { data: tenant } = useTenant(organization?.id);
  const websiteDomain = tenant?.domain?.primary || '';
  const organizationId = organization?.id || '';

  const isPreview = isPreviewContactId(contactId);
  const live = useContactSavedPropertiesGQL(isPreview ? '' : contactId);
  const items: any[] = isPreview ? PREVIEW_SAVED_PROPERTIES : live.items;
  const total = isPreview ? PREVIEW_SAVED_PROPERTIES.length : live.total;
  const hasNext = isPreview ? false : live.hasNext;
  const loading = isPreview ? false : live.loading;
  const handleNextPage = live.handleNextPage;

  const columns = React.useMemo(() => getColumns(websiteDomain, organizationId), [websiteDomain, organizationId]);

  return (
    <div className="flex flex-col gap-4">
      <ActivityTableHeader
        title="Saved Properties"
        count={total}
        isLoading={loading}
        showDateRange={false}
      />

      <TableCard
        borderless
        className="flex-shrink-0"
        bodyClassName="min-h-[200px] max-h-[640px] overflow-y-auto scrollbar-custom"
        footer={
          hasNext ? (
            <TablePagination
              hasNext={hasNext}
              onNextPage={handleNextPage}
              isLoading={loading}
            />
          ) : null
        }
      >
        <Table
          data={items}
          columns={columns}
          keyExtractor={(item) => item.propertyId}
          emptyStateText="No saved properties available"
          size="xl"
          loading={loading && items.length === 0}
        />
      </TableCard>
    </div>
  );
};
